// =============================================
// LISTA DE PARÁMETROS
// Pantalla principal para gestionar parámetros
// con CRUD completo
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  TextField,
  MenuItem,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import parameterService from '../../services/parameterService';
import ParameterForm from './ParameterForm';

const ParameterList = () => {
  // PARAMETRIZACIÓN: Estados del componente
  const [parameters, setParameters] = useState([]);
  const [filteredParameters, setFilteredParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // PARAMETRIZACIÓN: Estados de filtros
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // PARAMETRIZACIÓN: Cargar parámetros al montar el componente
  useEffect(() => {
    loadParameters();
  }, []);

  // PARAMETRIZACIÓN: Aplicar filtros cuando cambien
  useEffect(() => {
    filterParameters();
  }, [parameters, categoryFilter, searchTerm]);

  // PARAMETRIZACIÓN: Función para cargar todos los parámetros
  const loadParameters = async () => {
    try {
      setLoading(true);
      const response = await parameterService.getAll();
      setParameters(response.parameters || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar parámetros');
    } finally {
      setLoading(false);
    }
  };

  // PARAMETRIZACIÓN: Función para filtrar parámetros
  const filterParameters = () => {
    let filtered = [...parameters];

    // Filtrar por categoría
    if (categoryFilter) {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }

    // Filtrar por búsqueda (key, título o descripción)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.param_key.toLowerCase().includes(term) ||
        p.title.toLowerCase().includes(term) ||
        (p.description && p.description.toLowerCase().includes(term))
      );
    }

    setFilteredParameters(filtered);
  };

  // PARAMETRIZACIÓN: Abrir formulario para crear
  const handleCreate = () => {
    setSelectedParameter(null);
    setOpenForm(true);
  };

  // PARAMETRIZACIÓN: Abrir formulario para editar
  const handleEdit = (parameter) => {
    setSelectedParameter(parameter);
    setOpenForm(true);
  };

  // PARAMETRIZACIÓN: Cerrar formulario
  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedParameter(null);
  };

  // PARAMETRIZACIÓN: Manejar éxito al crear/editar
  const handleSuccess = () => {
    loadParameters();
    setSnackbar({
      open: true,
      message: selectedParameter ? 'Parámetro actualizado exitosamente' : 'Parámetro creado exitosamente',
      severity: 'success',
    });
  };

  // PARAMETRIZACIÓN: Eliminar parámetro
  const handleDelete = async (id, title, isEditable) => {
    // Prevenir eliminación de parámetros no editables
    if (!isEditable) {
      setSnackbar({
        open: true,
        message: 'Este parámetro no puede ser eliminado (es crítico del sistema)',
        severity: 'error',
      });
      return;
    }

    if (window.confirm(`¿Estás seguro de eliminar el parámetro "${title}"?`)) {
      try {
        await parameterService.delete(id);
        loadParameters();
        setSnackbar({
          open: true,
          message: 'Parámetro eliminado exitosamente',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error al eliminar parámetro',
          severity: 'error',
        });
      }
    }
  };

  // PARAMETRIZACIÓN: Cerrar snackbar de notificaciones
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // PARAMETRIZACIÓN: Obtener color para chip de categoría
  const getCategoryColor = (category) => {
    const colors = {
      general: 'default',
      security: 'error',
      payments: 'success',
      files: 'info',
      email: 'warning',
      notifications: 'secondary',
      system: 'primary',
    };
    return colors[category] || 'default';
  };

  // PARAMETRIZACIÓN: Obtener etiqueta traducida de categoría
  const getCategoryLabel = (category) => {
    const labels = {
      general: 'General',
      security: 'Seguridad',
      payments: 'Pagos',
      files: 'Archivos',
      email: 'Email',
      notifications: 'Notificaciones',
      system: 'Sistema',
    };
    return labels[category] || category;
  };

  // PARAMETRIZACIÓN: Obtener etiqueta traducida de tipo
  const getValueTypeLabel = (type) => {
    const labels = {
      string: 'Texto',
      number: 'Número',
      boolean: 'Booleano',
      json: 'JSON',
    };
    return labels[type] || type;
  };

  // PARAMETRIZACIÓN: Formatear valor para visualización
  const formatValue = (value, type) => {
    if (type === 'json' && value.length > 50) {
      return value.substring(0, 50) + '...';
    }
    return value;
  };

  // PARAMETRIZACIÓN: Obtener categorías únicas para el filtro
  const categories = [...new Set(parameters.map(p => p.category))];

  // PARAMETRIZACIÓN: Mostrar loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* PARAMETRIZACIÓN: Encabezado con botón crear */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Parámetros del Sistema
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Nuevo Parámetro
        </Button>
      </Box>

      {/* PARAMETRIZACIÓN: Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* PARAMETRIZACIÓN: Panel de filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Buscar por key, título o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Filtrar por Categoría"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">Todas las categorías</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {getCategoryLabel(cat)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* PARAMETRIZACIÓN: Tabla de parámetros */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredParameters.map((param) => (
              <TableRow key={param.id}>
                {/* PARAMETRIZACIÓN: Columna Key */}
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {param.param_key}
                  </Typography>
                </TableCell>

                {/* PARAMETRIZACIÓN: Columna Título con descripción */}
                <TableCell>
                  <Typography variant="body2">
                    {param.title}
                  </Typography>
                  {param.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {param.description}
                    </Typography>
                  )}
                </TableCell>

                {/* PARAMETRIZACIÓN: Columna Valor */}
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {formatValue(param.param_value, param.value_type)}
                  </Typography>
                </TableCell>

                {/* PARAMETRIZACIÓN: Columna Tipo */}
                <TableCell>
                  <Chip 
                    label={getValueTypeLabel(param.value_type)} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>

                {/* PARAMETRIZACIÓN: Columna Categoría */}
                <TableCell>
                  <Chip
                    label={getCategoryLabel(param.category)}
                    color={getCategoryColor(param.category)}
                    size="small"
                  />
                </TableCell>

                {/* PARAMETRIZACIÓN: Columna Estado */}
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Chip
                      label={param.is_active ? 'Activo' : 'Inactivo'}
                      color={param.is_active ? 'success' : 'default'}
                      size="small"
                    />
                    {!param.is_editable && (
                      <Tooltip title="No editable (parámetro crítico)">
                        <LockIcon color="action" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>

                {/* PARAMETRIZACIÓN: Columna Acciones */}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(param)}
                    title="Editar"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(param.id, param.title, param.is_editable)}
                    disabled={!param.is_editable}
                    title={param.is_editable ? "Eliminar" : "No se puede eliminar (parámetro crítico)"}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PARAMETRIZACIÓN: Mensaje cuando no hay resultados */}
      {filteredParameters.length === 0 && !loading && (
        <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {searchTerm || categoryFilter 
              ? 'No se encontraron parámetros con los filtros aplicados' 
              : 'No hay parámetros registrados'}
          </Typography>
        </Paper>
      )}

      {/* PARAMETRIZACIÓN: Formulario modal */}
      <ParameterForm
        open={openForm}
        onClose={handleCloseForm}
        parameter={selectedParameter}
        onSuccess={handleSuccess}
      />

      {/* PARAMETRIZACIÓN: Notificaciones (snackbar) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ParameterList;
